import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { hasPermission, PERMISSIONS } from "../utils/permissions"; 
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { FaPlus, FaUsers, FaEdit, FaEye } from "react-icons/fa";
import { DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import "../styles/common.css";
import "./Troupeaux.css";

export default function Troupeaux() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [troupeaux, setTroupeaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selectedBerger, setSelectedBerger] = useState('');
  const [bergers, setBergers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('troupeaux-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('troupeaux-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchTroupeaux = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedBerger) params.append('berger', selectedBerger);
        if (searchTerm) params.append('search', searchTerm);
        
        const response = await api.get("/api/troupeaux/", { params }); 
        const bergersRes = await api.get("/api/bergers/");
        setTroupeaux(response.data);
        setBergers(bergersRes.data);
        setLoading(false);
      } catch (err) {
        setError(t.errorLoadingHerds);
        setLoading(false);
      }
    };
    fetchTroupeaux();
  }, [selectedBerger, searchTerm]);


  // Fonction helper pour obtenir le nom du berger
  const getBergerName = (troupeau) => {
    // Si berger_nom est déjà une string, l'utiliser
    if (troupeau.berger_nom && typeof troupeau.berger_nom === 'string') {
      return troupeau.berger_nom;
    }
    
    return troupeau.berger ? t.notAssigned : "-";
  };

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    return troupeaux.map(troupeau => ({
      id: troupeau.id,
      nom: troupeau.nom,
      berger_nom: getBergerName(troupeau),
    }));
  }, [troupeaux]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'nom',
      headerName: t.herdName,
      width: 200,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'berger_nom',
      headerName: t.shepherd,
      width: 200,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
  ], [t]);

  const detailsColumn = useMemo(() => [
    {
      field: 'details',
      headerName: t.details,
      width: 130,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <button
          onClick={() => navigate(`/troupeaux/${params.row.id}/rapport`)}
          className="grid-action-button"
          style={{ fontSize: `${1.1 * zoomLevel}rem` }}
        >
          {t.viewMore}
        </button>
      ),
    },
  ], [navigate, zoomLevel, t]);

  const adminColumns = useMemo(() => {
    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_TROUPEAUX);
    if (!canEdit) return [];
    
    return [
      {
        field: 'actions',
        headerName: t.actions,
        width: 130,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <button
            onClick={() => navigate(`/troupeaux/${params.row.id}/modifier`)}
            className="grid-action-button"
            style={{ fontSize: `${1.3 * zoomLevel}rem` }}
          >
            {t.edit}
          </button>
        ),
      },
    ];
  }, [user, navigate, zoomLevel, t]);

  const columns = useMemo(() => {
    return adminColumns.length > 0 ? [...baseColumns, ...detailsColumn, ...adminColumns] : [...baseColumns, ...detailsColumn];
  }, [baseColumns, detailsColumn, adminColumns]);

  if (loading) {
    return (
      <div className="troupeaux-page">
        <div className="loading-container">
          <p className="loading-text">{t.loadingHerds}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="troupeaux-page">
        <div className="title-badge">
          <FaUsers className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="troupeaux-page">
      <div className="title-badge">
        <FaUsers className="page-title-icon" aria-hidden="true" />
        <span>{t.herdList}</span>
      </div>

      {/* Bouton d'action admin */}
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_TROUPEAUX)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-troupeau")}
          >
            <FaPlus />
            <span>{t.addHerd}</span>
          </button>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchByHerdName}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* Filtres */}
      <FiltersContainer title={t.filters}>
        <FilterSelect
          name="berger"
          value={selectedBerger}
          onChange={(e) => setSelectedBerger(e.target.value)}
          options={bergers && bergers.length > 0 ? bergers.map((b) => ({ 
            id: b.id, 
            nom: b.nom_complet || `${b.prenom || ""} ${b.nom || ""}`.trim() || "Sans nom"
          })) : []}
          placeholder={t.filterByShepherd}
          className="filtre-berger"
          width="200px"
        />
      </FiltersContainer>

      {/* DataGrid */}
      {troupeaux.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">{t.noHerdRegistered}</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="troupeaux-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'nom', sort: 'asc' }}
          pageSize={25}
          containerClassName="troupeaux-grid-container"
        />
      )}
    </div>
  );
}
